// QuestionPatientVisitDrugKitStatus.js
import React from "react";
import { QuestionTextModel, QuestionFactory, Serializer } from "survey-core";
import {
  ReactQuestionFactory,
  SurveyQuestionElementBase,
} from "survey-react-ui";

const QUESTION_TYPE = "patientVisitDrugKitStatus";
let isRegistered = false;

/* =========================================================
   QUESTION MODEL - Simple text input for status
========================================================= */
class QuestionPatientVisitDrugKitStatusModel extends QuestionTextModel {
  constructor(name) {
    super(name);

    // Set default properties
    this.inputType = "text";
    this.placeholder = "Select or enter drug kit status";
    this.autocomplete = "off";

    // Status-specific properties
    this.showStatusOptions = true;
    this.statusOptions = [
      "Active",
      "Dispensed",
      "Returned",
      "Expired",
      "Damaged",
    ];
    this.allowCustomInput = true;
  }

  getType() {
    return QUESTION_TYPE;
  }

  // Custom properties
  get showStatusOptions() {
    return this.getPropertyValue("showStatusOptions", true);
  }

  set showStatusOptions(val) {
    this.setPropertyValue("showStatusOptions", val);
  }

  get statusOptions() {
    return this.getPropertyValue("statusOptions", [
      "Active",
      "Dispensed",
      "Returned",
      "Expired",
      "Damaged",
    ]);
  }

  set statusOptions(val) {
    this.setPropertyValue("statusOptions", Array.isArray(val) ? val : []);
  }

  get allowCustomInput() {
    return this.getPropertyValue("allowCustomInput", true);
  }

  set allowCustomInput(val) {
    this.setPropertyValue("allowCustomInput", val);
  }
}

/* =========================================================
   REACT RENDERER with status selector
========================================================= */
class SurveyQuestionPatientVisitDrugKitStatus extends SurveyQuestionElementBase {
  constructor(props) {
    super(props);
    this.inputRef = React.createRef();
    this.state = {
      showDropdown: false,
    };
  }

  get question() {
    return this.props.question;
  }

  handleChange = (e) => {
    this.question.value = e.target.value || null;
  };

  handleSelectOption = (option) => {
    this.question.value = option;
    this.setState({ showDropdown: false });
  };

  toggleDropdown = () => {
    this.setState((prev) => ({ showDropdown: !prev.showDropdown }));
  };

  renderElement() {
    const q = this.question;
    const value = q.value ?? "";
    const { showDropdown } = this.state;

    return (
      <div style={{ position: "relative", width: "100%" }}>
        {/* Main input with dropdown button */}
        <div style={{ display: "flex", gap: "8px" }}>
          <input
            ref={this.inputRef}
            type="text"
            id={q.id}
            name={q.name}
            value={value}
            onChange={this.handleChange}
            onBlur={q.onBlur?.bind(q)}
            onFocus={q.onFocus?.bind(q)}
            placeholder={q.placeholder}
            disabled={q.isReadOnly}
            required={q.isRequired}
            style={{
              flex: 1,
              boxSizing: "border-box",
              padding: "8px 12px",
              paddingRight: "40px",
              borderRadius: "4px",
              border:
                q.errors.length > 0 ? "1px solid #dc3545" : "1px solid #ced4da",
              backgroundColor: q.isReadOnly ? "#e9ecef" : "#fff",
              fontSize: "16px",
              height: "38px",
            }}
          />

          {/* Dropdown toggle button */}
          {q.showStatusOptions && !q.isReadOnly && (
            <button
              type="button"
              onClick={this.toggleDropdown}
              style={{
                padding: "8px 16px",
                backgroundColor: "#007bff",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "14px",
                whiteSpace: "nowrap",
              }}
            >
              Status ▼
            </button>
          )}
        </div>

        {/* Status options dropdown */}
        {q.showStatusOptions && showDropdown && !q.isReadOnly && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              backgroundColor: "white",
              border: "1px solid #ced4da",
              borderRadius: "4px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              zIndex: 1000,
              marginTop: "4px",
              maxHeight: "200px",
              overflowY: "auto",
            }}
          >
            {q.statusOptions.map((option, index) => (
              <button
                key={index}
                type="button"
                onClick={() => this.handleSelectOption(option)}
                style={{
                  width: "100%",
                  padding: "10px 16px",
                  backgroundColor: value === option ? "#e7f3ff" : "transparent",
                  border: "none",
                  borderBottom: "1px solid #f8f9fa",
                  textAlign: "left",
                  cursor: "pointer",
                  fontSize: "14px",
                  color: "#212529",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  transition: "background-color 0.2s",
                }}
                onMouseOver={(e) =>
                  (e.target.style.backgroundColor = "#f8f9fa")
                }
                onMouseOut={(e) =>
                  (e.target.style.backgroundColor =
                    value === option ? "#e7f3ff" : "transparent")
                }
              >
                <div
                  style={{
                    width: "12px",
                    height: "12px",
                    borderRadius: "50%",
                    backgroundColor: this.getStatusColor(option),
                  }}
                />
                {option}
              </button>
            ))}

            {/* Custom input option */}
            {q.allowCustomInput && (
              <div
                style={{
                  padding: "12px 16px",
                  borderTop: "1px solid #dee2e6",
                  backgroundColor: "#f8f9fa",
                }}
              >
                <input
                  type="text"
                  placeholder="Enter custom status..."
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && e.target.value.trim()) {
                      this.handleSelectOption(e.target.value.trim());
                      e.target.value = "";
                    }
                  }}
                  style={{
                    width: "100%",
                    padding: "6px 12px",
                    border: "1px solid #ced4da",
                    borderRadius: "4px",
                    fontSize: "14px",
                  }}
                />
                <div
                  style={{
                    fontSize: "12px",
                    color: "#6c757d",
                    marginTop: "4px",
                  }}
                >
                  Press Enter to add custom status
                </div>
              </div>
            )}
          </div>
        )}

        {/* Status legend */}
        {q.showStatusOptions && (
          <div
            style={{
              display: "flex",
              gap: "12px",
              flexWrap: "wrap",
              marginTop: "8px",
              fontSize: "12px",
              color: "#6c757d",
            }}
          >
            {q.statusOptions.map((option, index) => (
              <div
                key={index}
                style={{ display: "flex", alignItems: "center", gap: "4px" }}
              >
                <div
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: this.getStatusColor(option),
                  }}
                />
                {option}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  getStatusColor(status) {
    const colorMap = {
      Active: "#28a745",
      Dispensed: "#007bff",
      Returned: "#6c757d",
      Expired: "#dc3545",
      Damaged: "#ffc107",
    };
    return colorMap[status] || "#6c757d";
  }
}

/* =========================================================
   REGISTRATION with EXPLICIT text type definitions
========================================================= */
export function registerPatientVisitDrugKitStatusQuestion() {
  if (isRegistered) return;
  isRegistered = true;

  // Define ALL properties with explicit text types
  Serializer.addClass(
    QUESTION_TYPE,
    [
      // ✅ CRITICAL: Explicit text type for name to prevent date detection
      {
        name: "name",
        type: "text", // Explicit type
        category: "general",
        visible: true,
        isRequired: true,
        isLocalizable: false,
        onSetValue: function (obj, value) {
          obj.setPropertyValue("name", value);
        },
        onGetValue: function (obj) {
          return obj.getPropertyValue("name");
        },
      },
      {
        name: "title",
        type: "text", // Explicit type
        category: "general",
        visible: true,
      },
      {
        name: "description",
        type: "text", // Explicit type
        category: "general",
        visible: true,
      },
      {
        name: "isRequired:boolean",
        category: "general",
        visible: true,
      },
      {
        name: "readOnly:boolean",
        category: "general",
        visible: true,
      },
      {
        name: "placeholder",
        type: "text", // Explicit type
        category: "general",
        default: "Select or enter drug kit status",
        visible: true,
      },
      // Status-specific properties
      {
        name: "showStatusOptions:boolean",
        category: "status",
        default: true,
        visible: true,
      },
      {
        name: "statusOptions",
        category: "status",
        visible: true,
        onSetValue: function (obj, value) {
          if (Array.isArray(value)) {
            obj.statusOptions = value;
          }
        },
        onGetValue: function (obj) {
          return obj.statusOptions;
        },
      },
      {
        name: "allowCustomInput:boolean",
        category: "status",
        default: true,
        visible: true,
      },
    ],
    () => new QuestionPatientVisitDrugKitStatusModel(""),
    "text"
  );

  // ✅ Force hide unnecessary inherited properties
  const unnecessaryProps = ["inputType", "size", "maxLength", "autocomplete"];
  unnecessaryProps.forEach((propName) => {
    const prop = Serializer.findProperty(QUESTION_TYPE, propName);
    if (prop) {
      prop.visible = false;
    }
  });

  // ✅ Force inputType to "text" and make it read-only
  const inputTypeProp = Serializer.findProperty(QUESTION_TYPE, "inputType");
  if (inputTypeProp) {
    inputTypeProp.defaultValue = "text";
    inputTypeProp.readOnly = true;
    inputTypeProp.visible = false;
  }

  // ✅ Additional safeguard: Manually ensure name property stays as text
  setTimeout(() => {
    const nameProp = Serializer.findProperty(QUESTION_TYPE, "name");
    if (nameProp) {
      nameProp.type = "text";
      nameProp.inputType = "text";
      nameProp.isLocalizable = false;
      nameProp.isRequired = true;

      // Remove any date-specific settings
      delete nameProp.dateFormat;
      delete nameProp.minDate;
      delete nameProp.maxDate;
    }
  }, 0);

  // Register with QuestionFactory
  QuestionFactory.Instance.registerQuestion(
    QUESTION_TYPE,
    (name) => new QuestionPatientVisitDrugKitStatusModel(name)
  );

  // Register React component
  ReactQuestionFactory.Instance.registerQuestion(QUESTION_TYPE, (props) =>
    React.createElement(SurveyQuestionPatientVisitDrugKitStatus, props)
  );
}

// Auto-register
registerPatientVisitDrugKitStatusQuestion();

export default QuestionPatientVisitDrugKitStatusModel;
